/**
 * Géométrie de la scène de chute.
 *
 * Ce module n'importe rien — un test lit le fichier et le vérifie. Il ne peut
 * donc ni intégrer une trajectoire, ni décider d'une pesanteur : les hauteurs
 * lui sont **données**, en mètres, par l'appelant qui les tient du noyau. Ce
 * qu'il décide, ce sont des positions dans une scène, et rien d'autre.
 *
 * ## Ce que la scène montre, et ce qu'elle ne montre pas
 *
 * Deux corps tombent côte à côte, un par modèle. Ils partent du même point et
 * de la même hauteur ; ce qui les sépare à l'écran est **entièrement** ce que
 * les modèles prédisent. Il n'y a pas d'échelle trompeuse ici, contrairement au
 * schéma d'Ératosthène où le gnomon est démesuré : la hauteur de lâcher vaut
 * un, et toutes les hauteurs sont dans ce rapport-là. Ce qu'on voit est ce que
 * la table chiffre.
 *
 * La scène ne départage rien non plus. Voir un corps toucher le sol avant
 * l'autre ne dit pas lequel a raison — il faudrait pour ça lâcher deux vrais
 * objets et les chronométrer. Elle rend visible un désaccord, pas un verdict.
 *
 * Hors du noyau : nombres nus et `Math` autorisés (les unités ici sont des
 * unités de scène, pas des grandeurs physiques).
 */

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Ce que l'appelant doit fournir. Aucun de ces nombres n'est décidé ici. */
export interface FallSceneRequest {
  /** Hauteur de lâcher, en mètres. Fixe l'échelle de toute la scène. */
  readonly dropHeight: number;
  readonly bodies: readonly FallBodyRequest[];
}

export interface FallBodyRequest {
  readonly id: string;
  readonly label: string;
  /**
   * Hauteur au-dessus du sol, en mètres. `undefined` quand le modèle ne prédit
   * rien : le corps n'est alors pas dessiné, et on n'en invente pas la place.
   */
  readonly height: number | undefined;
}

export interface FallBodySketch {
  readonly id: string;
  readonly label: string;
  readonly centre: Vec3;
  readonly radius: number;
  /** Le rail du lâcher au sol : il rend la hauteur lisible sur une image fixe. */
  readonly track: readonly [Vec3, Vec3];
  /** Vrai quand le corps a touché le sol. */
  readonly landed: boolean;
  /** Fraction de la chute déjà parcourue, de 0 au lâcher à 1 au sol. */
  readonly progress: number;
}

export interface FallScene {
  readonly bodies: readonly FallBodySketch[];
  readonly groundRadius: number;
  /** Hauteur de lâcher, en unités de scène. Vaut toujours `DROP`. */
  readonly releaseHeight: number;
  readonly bounds: { readonly min: Vec3; readonly max: Vec3 };
}

/** Hauteur de lâcher en unités de scène : toute la scène s'y rapporte. */
const DROP = 1;
/**
 * Le sol est là pour poser la scène, pas pour l'occuper : au-delà, la chute
 * devient un détail au milieu d'une esplanade.
 */
const GROUND_RADIUS = 0.8;
const BODY_RADIUS = 0.055;
/** Écart latéral entre les deux corps : assez pour ne pas se recouvrir. */
const SPACING = 0.28;

function vec(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

/**
 * Positions latérales centrées sur zéro : un corps au milieu, deux de part et
 * d'autre, et ainsi de suite. Aucun modèle n'occupe une place privilégiée.
 */
export function lateralOffsets(count: number): readonly number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const first = -((count - 1) * SPACING) / 2;
  return Array.from({ length: count }, (_, index) => first + index * SPACING);
}

export function buildFallScene(request: FallSceneRequest): FallScene {
  const drop = request.dropHeight > 0 ? request.dropHeight : 1;
  const offsets = lateralOffsets(request.bodies.length);

  const bodies: FallBodySketch[] = [];
  request.bodies.forEach((body, index) => {
    if (body.height === undefined) return;

    // La hauteur de lâcher vaut un : le rapport est fidèle, et c'est le seul
    // engagement que ce dessin prend.
    const scaled = Math.max(0, Math.min(DROP, (body.height / drop) * DROP));
    const x = offsets[index] ?? 0;

    bodies.push({
      id: body.id,
      label: body.label,
      centre: vec(x, scaled + BODY_RADIUS, 0),
      radius: BODY_RADIUS,
      track: [vec(x, DROP + BODY_RADIUS, 0), vec(x, 0, 0)],
      landed: scaled <= 0,
      progress: 1 - scaled / DROP,
    });
  });

  return {
    bodies,
    groundRadius: GROUND_RADIUS,
    releaseHeight: DROP,
    bounds: {
      min: vec(-GROUND_RADIUS, 0, -GROUND_RADIUS),
      max: vec(GROUND_RADIUS, DROP + 2 * BODY_RADIUS, GROUND_RADIUS),
    },
  };
}

/** Le centre de la boîte englobante — ce que la caméra vise. */
export function fallBoundsCentre(scene: FallScene): Vec3 {
  const { min, max } = scene.bounds;
  return vec((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2);
}

/** Le rayon de la sphère englobante. */
export function fallBoundsRadius(scene: FallScene): number {
  const centre = fallBoundsCentre(scene);
  const { min, max } = scene.bounds;
  const dx = Math.max(max.x - centre.x, centre.x - min.x);
  const dy = Math.max(max.y - centre.y, centre.y - min.y);
  const dz = Math.max(max.z - centre.z, centre.z - min.z);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

const RIM_SAMPLES = 24;

/**
 * Les points **réellement occupés** par la scène, pour le cadrage.
 *
 * La boîte englobante ne suffit pas, et ce n'est pas une subtilité : le sol est
 * un **disque**, ses coins de boîte ne contiennent rien, et cadrer dessus
 * reculait la caméra jusqu'à ne remplir qu'un cinquième du cadre. Le schéma
 * d'Ératosthène avait exactement ce défaut, pour exactement cette raison. On
 * échantillonne donc ce qui existe au lieu de majorer ce qui pourrait exister.
 */
export function fallScenePoints(scene: FallScene): readonly Vec3[] {
  const points: Vec3[] = [];

  for (let index = 0; index < RIM_SAMPLES; index += 1) {
    const angle = (2 * Math.PI * index) / RIM_SAMPLES;
    points.push(vec(scene.groundRadius * Math.cos(angle), 0, scene.groundRadius * Math.sin(angle)));
  }

  for (const body of scene.bodies) {
    const [top] = body.track;
    points.push(top);
    // La bille a une épaisseur : la cadrer par son seul centre la ferait
    // affleurer le bord.
    for (const offset of [-body.radius, body.radius]) {
      points.push(vec(body.centre.x + offset, body.centre.y, body.centre.z));
      points.push(vec(body.centre.x, body.centre.y + offset, body.centre.z));
      points.push(vec(body.centre.x, body.centre.y, body.centre.z + offset));
    }
  }

  return points;
}

/** Quadrillage du sol, en segments. Sans lui, le disque est une tache plate. */
export function groundGrid(radius: number): readonly (readonly [Vec3, Vec3])[] {
  const lines: (readonly [Vec3, Vec3])[] = [];
  const steps = 8;
  for (let index = 0; index <= steps; index += 1) {
    const t = -radius + (2 * radius * index) / steps;
    const half = Math.sqrt(Math.max(0, radius * radius - t * t));
    lines.push([vec(t, 0, -half), vec(t, 0, half)]);
    lines.push([vec(-half, 0, t), vec(half, 0, t)]);
  }
  return lines;
}

export const DRAWN_DROP = DROP;
export const DRAWN_BODY_RADIUS = BODY_RADIUS;
